import { RefreshCw } from 'lucide-react';
import { useId } from 'react';
import type { Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@nucleus-commerce/ui/components/button';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import { Switch } from '@nucleus-commerce/ui/components/switch';

import type { TryItValues } from '../../schemas/try-it-form-schema';

/**
 * A fresh UUID per send, editable. "Reuse last" sends the previous key again so the replayed
 * response (`idempotency-replayed: true`) can be seen.
 */
export function IdempotencyField({
  control,
  reuseLast,
  canReuse,
  onReuseLastChange,
  onRegenerate,
}: {
  control: Control<TryItValues>;
  reuseLast: boolean;
  canReuse: boolean;
  onReuseLastChange: (reuse: boolean) => void;
  onRegenerate: () => void;
}) {
  const { t } = useTranslation('docs');
  const switchId = useId();
  return (
    <FormField
      control={control}
      name="idempotencyKey"
      render={({ field }) => (
        <FormItem className="gap-1.5">
          <FormLabel className="font-mono text-xs">Idempotency-Key</FormLabel>
          <div className="flex gap-2">
            <FormControl>
              <Input
                autoComplete="off"
                spellCheck={false}
                className="h-8 font-mono text-[12.5px]"
                readOnly={reuseLast}
                {...field}
              />
            </FormControl>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="size-8"
              aria-label={t('tryIt.idempotency.regenerate')}
              disabled={reuseLast}
              onClick={onRegenerate}
            >
              <RefreshCw aria-hidden />
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id={switchId}
              checked={reuseLast}
              disabled={!canReuse}
              onCheckedChange={onReuseLastChange}
            />
            <Label htmlFor={switchId} className="text-xs font-normal">
              {t('tryIt.idempotency.reuseLast')}
            </Label>
          </div>
          <FormDescription>{t('tryIt.idempotency.hint')}</FormDescription>
        </FormItem>
      )}
    />
  );
}
